/**
 * ⌘K, doing what the sidebar has been promising.
 *
 * The affordance shipped with the shell and was mostly inert: it focused
 * `[data-screen-search]`, which exists on three of thirteen screens, so on the
 * other ten the advertised shortcut did nothing at all. A visible keyboard
 * shortcut that silently does nothing is worse than none — it teaches people
 * that the keyboard does not work here.
 *
 * TWO TIERS, AND THE SPLIT IS HONEST ABOUT THE API.
 *
 *   Navigation always. Every route the sidebar shows — handed over by the shell
 *   already filtered with the sidebar's own rule — matched on the label. This
 *   alone is most of the value: it makes the sidebar's depth stop mattering,
 *   which is what the whole shell pass was about.
 *
 *   Records only where the server can actually search them. Contacts have
 *   `?search=` and messages have `?to=`; templates, campaigns and groups have
 *   no server-side search, and fetching every one of them to filter in the
 *   browser would work on the demo database and quietly stop working on a real
 *   one. A palette that is fast for the author and useless at scale is the
 *   failure mode worth avoiding here.
 *
 * The per-screen search inputs stay exactly as they are. Filtering a list in
 * place and jumping to a record are different jobs, and the palette is bad at
 * the first one — it cannot leave a filtered list on screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../api/client";
import { Icon, type IconName } from "../components/Sprites";
import { useSession } from "../session";
import type { Contact, CursorPage, Message } from "../api/types";

/** One thing the palette can do. */
interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: IconName;
  to: string;
  group: string;
}

/**
 * How long to wait after a keystroke before asking the server.
 *
 * Long enough that typing an address is one request rather than twenty, short
 * enough that it still feels like the list is following you.
 */
const SEARCH_DEBOUNCE_MS = 220;

/** Below this, only navigation is offered — one letter matches everything. */
const MIN_QUERY = 2;

/** Enough to choose from; more than this is a list nobody reads. */
const MAX_RECORDS = 5;

export function CommandPalette({
  open,
  onClose,
  routes,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * The screens to offer, already filtered by the shell with the sidebar's own
   * rule. Deliberately no `needs` here: a field this component could filter on
   * is an invitation to filter again, and a second copy of the rule is how
   * Tenants reached every user.
   */
  routes: { to: string; label: string; icon: IconName }[];
}) {
  const navigate = useNavigate();
  const { can, orgId } = useSession();

  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [records, setRecords] = useState<Command[]>([]);
  const [searching, setSearching] = useState(false);

  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  // Where focus was before the palette opened. Restoring it is what makes this
  // an overlay rather than a navigation: dismissing without choosing has to
  // leave the keyboard exactly where it was.
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  const navigation = useMemo<Command[]>(
    () =>
      routes.map((r) => ({ id: `nav:${r.to}`, label: r.label, icon: r.icon, to: r.to, group: "Go to" })),
    [routes],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navigation;
    return navigation.filter((c) => c.label.toLowerCase().includes(q));
  }, [navigation, query]);

  const results = useMemo(() => [...matches, ...records], [matches, records]);

  // Reset on open rather than on close, so the closing animation-free unmount
  // does not flash an empty list, and so re-opening never shows the previous
  // search's results against a new organization.
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    setQuery("");
    setRecords([]);
    setCursor(0);
    // The input mounts with the palette, so focus after paint.
    const id = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (open) return;
    restoreFocusTo.current?.focus();
  }, [open]);

  // Record search. Debounced and abortable — every keystroke supersedes the
  // last, and an in-flight response for "pri" must never land after "priya".
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < MIN_QUERY) {
      setRecords([]);
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    setSearching(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        const found: Command[] = [];

        // Each search is guarded by its own capability rather than by one
        // "can search" flag: a key holding messages:read and not contacts:read
        // is an ordinary thing to issue, and the palette should offer it the
        // half it can have instead of nothing.
        const wanted: Promise<void>[] = [];

        if (can("contacts:read")) {
          wanted.push(
            api
              .get<CursorPage<Contact>>("/v1/contacts", {
                query: { search: q, limit: MAX_RECORDS },
                signal: controller.signal,
              })
              .then((page) => {
                for (const contact of page.data) {
                  found.push({
                    id: `contact:${contact.id}`,
                    label: contact.name || primaryAddress(contact) || "Unnamed contact",
                    hint: contact.name ? primaryAddress(contact) : undefined,
                    icon: "contacts",
                    to: `/contacts/${contact.id}`,
                    group: "Contacts",
                  });
                }
              })
              .catch(swallowAbort),
          );
        }

        if (can("messages:read")) {
          wanted.push(
            api
              .get<CursorPage<Message>>("/v1/messages", {
                query: { to: q, limit: MAX_RECORDS },
                signal: controller.signal,
              })
              .then((page) => {
                for (const message of page.data) {
                  found.push({
                    id: `message:${message.id}`,
                    label: message.to,
                    hint: message.subject || message.status,
                    icon: "log",
                    to: `/messages/${message.id}`,
                    group: "Messages",
                  });
                }
              })
              .catch(swallowAbort),
          );
        }

        await Promise.all(wanted);
        if (controller.signal.aborted) return;
        setRecords(found);
        setSearching(false);
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, open, can, orgId]);

  // The highlight has to stay inside the list as it shrinks, or Enter runs
  // whatever happens to be last — which on a shrinking list is not what the
  // person was looking at when they pressed it.
  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const run = useCallback(
    (command: Command) => {
      onClose();
      navigate(command.to);
    },
    [navigate, onClose],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    // A key this palette handles belongs to this palette and must not reach
    // anything underneath it.
    //
    // ESCAPE CLOSED BOTH THE PALETTE AND THE DRAWER BEHIND IT. MessageDrawer,
    // ContactDrawer and ImportContacts each bind Escape with
    // document.addEventListener, and React delegates from the root element —
    // which is inside document — so React's handler runs first and the drawer's
    // runs immediately after. One keystroke dismissed two layers and navigated
    // back to the list, which is precisely what somebody pressing Escape over a
    // palette does not want. Found by opening a message, pressing ⌘K, and
    // pressing Escape.
    //
    // stopPropagation is enough because those listeners are on an ancestor. It
    // would not be if one were added to the same node, which is worth knowing
    // before somebody moves one.
    const claim = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (event.key === "Escape") {
      claim();
      onClose();
      return;
    }
    if (event.key === "ArrowDown" || (event.key === "n" && event.ctrlKey)) {
      claim();
      setCursor((c) => (results.length === 0 ? 0 : (c + 1) % results.length));
      return;
    }
    if (event.key === "ArrowUp" || (event.key === "p" && event.ctrlKey)) {
      claim();
      setCursor((c) => (results.length === 0 ? 0 : (c - 1 + results.length) % results.length));
      return;
    }
    if (event.key === "Enter") {
      claim();
      const chosen = results[cursor];
      if (chosen) run(chosen);
    }
  };

  // Keep the highlighted row visible when the arrow keys walk past the fold.
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (!open) return null;

  let index = -1;
  let lastGroup = "";

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} />
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cmdk__field">
          <Icon name="search" size={15} />
          <input
            ref={input}
            className="cmdk__input"
            placeholder="Jump to a screen, a contact, or a message…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
          <span className="oc-kbd">esc</span>
        </div>

        <div className="cmdk__results" ref={list} role="listbox" aria-label="Results">
          {results.length === 0 && (
            <p className="cmdk__empty">
              {searching ? "Searching…" : `Nothing matches “${query.trim()}”.`}
              {!searching && can("messages:read") && (
                // Said here rather than discovered: `to` matches from the start
                // of the address, so searching a domain returns nothing and
                // looks broken. The spec says this; the palette is where
                // somebody actually meets it.
                <span className="oc-text-muted">
                  {" "}
                  Message search matches the start of an address, so a domain on its own finds
                  nothing.
                </span>
              )}
            </p>
          )}

          {results.map((command) => {
            index += 1;
            const heading = command.group !== lastGroup ? command.group : null;
            lastGroup = command.group;
            const selected = index === cursor;
            const position = index;

            return (
              <div key={command.id}>
                {heading && <div className="cmdk__group">{heading}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="cmdk__option"
                  onClick={() => run(command)}
                  onMouseMove={() => setCursor(position)}
                >
                  <Icon name={command.icon} size={14} />
                  <span className="oc-truncate">{command.label}</span>
                  {command.hint && (
                    <span className="cmdk__hint oc-truncate oc-text-muted">{command.hint}</span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/**
 * The address to show beside a contact's name.
 *
 * Email first rather than the first identity in the array: the palette is used
 * to find somebody you are about to look at in a mail product, and an SMS
 * number is not what identifies them there.
 */
function primaryAddress(contact: Contact): string | undefined {
  const email = contact.identities?.find((i) => i.channel === "email");
  return (email ?? contact.identities?.[0])?.address;
}

/**
 * An aborted request is the normal case here — every keystroke cancels the last
 * one — so it must not reach the error path. Anything else is swallowed too:
 * the palette degrades to navigation, which is the tier that always works, and
 * a failed record search is not worth an error state over a list that is still
 * useful.
 */
function swallowAbort(error: unknown): void {
  if (error instanceof DOMException && error.name === "AbortError") return;
}
